import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { BankRuleService } from '../../services/bankRule.service';
import { ACTION_LABEL, BankRuleApplyResult, BankRulePreview, BankRulePreviewRow } from '../../interfaces/bank/bankRule.interface';
import { formatDate } from '../../utils/dateUtils';

const formatAmount = (value: number) =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Props {
  reconciliationId: string;
  onClose: () => void;
  /** Called once when the dialog closes after an apply ran (even partly), so the workspace refreshes. It is NOT called
   *  while the dialog is open: the workspace shows a loading state on refresh, which would unmount this dialog and
   *  wipe the per-line results. */
  onApplied: () => void;
}

/** Preview of the unmatched statement lines a bank rule matches, then "Apply selected". The preview never
 *  posts; each selected line is applied in its own transaction and reports its own outcome. */
const ApplyRulesDialog: React.FC<Props> = ({ reconciliationId, onClose, onApplied }) => {
  const toast = useToast();
  const [preview, setPreview] = useState<BankRulePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<BankRuleApplyResult | null>(null);
  const [didApply, setDidApply] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const p = await BankRuleService.preview(reconciliationId);
      setPreview(p);
      setSelected(new Set());
    } catch (e) {
      toast.showApiError(e, 'Failed to evaluate bank rules');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconciliationId]);

  useEffect(() => { load(); }, [load]);

  const rows = preview?.rows ?? [];
  const clean = rows.filter(r => r.warnings.length === 0);

  const toggle = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleApply = async () => {
    const items = rows.filter(r => selected.has(r.lineId)).map(r => ({ lineId: r.lineId, ruleId: r.ruleId }));
    if (items.length === 0) return;
    try {
      setApplying(true);
      const res = await BankRuleService.apply(reconciliationId, items);
      setResult(res);
      if (res.failed === 0) toast.showSuccess(`${res.applied} line${res.applied === 1 ? '' : 's'} posted and matched`);
      else toast.showError(`${res.applied} applied, ${res.failed} failed — see the results below`);
      setDidApply(true);
    } catch (e) {
      toast.showApiError(e, 'Failed to apply bank rules');
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    onClose();
    if (didApply) onApplied();
  };

  const rowOf = (lineId: string): BankRulePreviewRow | undefined => rows.find(r => r.lineId === lineId);

  return (
    <Dialog open onClose={handleClose} maxWidth="lg" fullWidth PaperProps={{ 'data-help-page': 'bank.applyRules.page' } as any}>
      <DialogTitle>Apply bank rules</DialogTitle>
      <DialogContent>
        {loading && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>}
        {!loading && preview && !result && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {preview.matchedByRule} of {preview.unmatchedLines} unmatched line{preview.unmatchedLines === 1 ? '' : 's'} match a rule.
              Nothing is posted until you apply the lines you select.
            </Typography>
            {rows.length === 0 ? (
              <Alert severity="info">No unmatched statement line matches an active bank rule.</Alert>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  <Button data-help="banking.applyRules.selectClean" size="small" onClick={() => setSelected(new Set(clean.map(r => r.lineId)))}>Select all without warnings</Button>
                  <Button data-help="banking.applyRules.clear" size="small" onClick={() => setSelected(new Set())}>Clear selection</Button>
                </Box>
                <TableContainer data-help-section="banking.applyRules.table">
                  <Table size="small" data-testid="apply-rules-table">
                    <TableHead>
                      <TableRow>
                        <TableCell data-help="banking.applyRules.col.select" padding="checkbox" />
                        <TableCell data-help="banking.applyRules.col.date">Date</TableCell>
                        <TableCell data-help="banking.applyRules.col.description">Description</TableCell>
                        <TableCell data-help="banking.applyRules.col.amount" align="right">Amount</TableCell>
                        <TableCell data-help="banking.applyRules.col.rule">Rule</TableCell>
                        <TableCell data-help="banking.applyRules.col.action">Action</TableCell>
                        <TableCell data-help="banking.applyRules.col.warnings">Warnings</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(r => (
                        <TableRow key={r.lineId} hover>
                          <TableCell padding="checkbox">
                            <Checkbox data-help="bank.applyRules.rowSelect" checked={selected.has(r.lineId)} onChange={() => toggle(r.lineId)}
                              inputProps={{ 'aria-label': `Select ${r.description ?? r.lineId}` }} />
                          </TableCell>
                          <TableCell>{formatDate(r.lineDate)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'normal', minWidth: 160 }}>{r.description || '—'}</TableCell>
                          <TableCell align="right">{formatAmount(r.amount)}</TableCell>
                          <TableCell>{r.ruleName}</TableCell>
                          <TableCell sx={{ whiteSpace: 'normal' }}>
                            {ACTION_LABEL[r.actionType] ?? r.actionType}
                            {(r.targetLabel || r.accountLabel) ? ` · ${r.targetLabel || r.accountLabel}` : ''}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'normal' }}>
                            {r.warnings.length === 0 ? '—' : r.warnings.map(w => (
                              <Chip key={w} size="small" color="warning" variant="outlined" label={w} sx={{ mr: 0.5, mb: 0.5, height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }} />
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
        {result && (
          <Box data-testid="apply-rules-result">
            <Alert severity={result.failed === 0 ? 'success' : 'warning'} sx={{ mb: 1.5 }}>
              {result.applied} applied, {result.failed} failed (of {result.requested}).
            </Alert>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell data-help="banking.applyRules.result.col.line">Line</TableCell><TableCell data-help="banking.applyRules.result.col.rule">Rule</TableCell><TableCell data-help="banking.applyRules.result.col.outcome">Outcome</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {result.results.map(r => {
                    const row = rowOf(r.lineId);
                    return (
                      <TableRow key={r.lineId}>
                        <TableCell>{row ? `${formatDate(row.lineDate)} · ${row.description || '—'}` : r.lineId}</TableCell>
                        <TableCell>{row?.ruleName ?? ''}</TableCell>
                        <TableCell>
                          <Chip size="small" color={r.status === 'APPLIED' ? 'success' : 'error'} label={r.status === 'APPLIED' ? 'Applied' : 'Failed'} />
                          {r.message ? <Typography variant="caption" sx={{ ml: 1 }}>{r.message}</Typography> : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <>
            <Button data-help="bank.applyRules.reviewRemaining" onClick={() => { setResult(null); load(); }}>Review remaining lines</Button>
            <Button data-help="bank.applyRules.close" variant="contained" onClick={handleClose}>Close</Button>
          </>
        ) : (
          <>
            <Button data-help="bank.applyRules.cancel" onClick={handleClose}>Cancel</Button>
            <Button data-help="banking.applyRules.apply" variant="contained" disabled={applying || selected.size === 0} onClick={handleApply}>
              {applying ? 'Applying…' : `Apply selected (${selected.size})`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApplyRulesDialog;
